import React, { useState } from 'react';
import { ChevronLeft, AlertTriangle, Clock, CheckCircle2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FeatureSteps } from '@/components/ui/feature-steps';

// Import images
import step1Settings from '@/assets/find-my/step-1-settings.png';
import step2AppleId from '@/assets/find-my/step-2-apple-id.png';
import step3FindMy from '@/assets/find-my/step-3-find-my.png';
import step4FindMyiPhone from '@/assets/find-my/step-4-find-my-iphone.png';
import step4ToggleOff from '@/assets/find-my/step-4-toggle-off.png';
import step4ToggleOffResult from '@/assets/find-my/step-4-toggle-off-result.png';
import stolenDeviceSearch from '@/assets/find-my/stolen-device-search.png';
import stolenDeviceToggle from '@/assets/find-my/stolen-device-toggle.png';
import stolenDeviceOff from '@/assets/find-my/stolen-device-off.png';
import stolenDeviceWaiting from '@/assets/find-my/stolen-device-waiting.png';
import stolenDeviceReturnFindMy from '@/assets/find-my/stolen-device-return-findmy.png';
import stolenDeviceTurnOffAfterDelay from '@/assets/find-my/stolen-device-turn-off-after-delay.png';
import stolenDeviceSettings from '@/assets/find-my/stolen-device-settings.png';
import stolenDevicePrivacySecurity from '@/assets/find-my/stolen-device-privacy-security.png';
import stolenDeviceTapStolen from '@/assets/find-my/stolen-device-tap-stolen.png';

interface FindMyGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper to create Find My steps with action callback
const createFindMySteps = (onCantTurnOff: () => void) => [
  {
    step: 'Step 1',
    title: 'Open Settings',
    content: 'On your iPhone, tap the Settings app on your home screen.',
    image: step1Settings,
    pointerTarget: [12, 14] as [number, number],
  },
  {
    step: 'Step 2',
    title: 'Tap Your Apple ID',
    content: 'At the top of Settings, tap your name to open your Apple Account settings.',
    image: step2AppleId,
    pointerTarget: [75, 30] as [number, number],
  },
  {
    step: 'Step 3',
    title: 'Tap Find My',
    content: 'Scroll down and tap "Find My" to open the Find My settings.',
    image: step3FindMy,
    pointerTarget: [75, 58] as [number, number],
  },
  {
    step: 'Step 4',
    title: 'Tap Find My iPhone',
    content: 'Tap on "Find My iPhone" where it shows "On" to open the toggle settings.',
    image: step4FindMyiPhone,
    pointerTarget: [75, 22] as [number, number],
  },
  {
    step: 'Step 5',
    title: 'Turn Off Find My iPhone',
    content: 'Toggle "Find My iPhone" to OFF and enter your Apple ID password.',
    image: step4ToggleOff,
    imageAfterTap: step4ToggleOffResult,
    pointerTarget: [75, 18] as [number, number],
    actions: [
      {
        label: "Can't turn it off? Stolen Device Protection might be on",
        onClick: onCantTurnOff,
        variant: 'outline' as const,
      },
    ],
  },
];

// Stolen Device Protection steps
const STOLEN_DEVICE_STEPS = [
  {
    step: 'Step 1',
    title: 'Go to Settings',
    content: 'Open the Settings app on your iPhone.',
    image: stolenDeviceSettings,
  },
  {
    step: 'Step 2',
    title: 'Open Privacy & Security',
    content: 'Scroll down and tap "Privacy & Security".',
    image: stolenDevicePrivacySecurity,
    pointerTarget: [50, 52] as [number, number], // Points to Privacy & Security row
  },
  {
    step: 'Step 3',
    title: 'Tap Stolen Device Protection',
    content: 'Scroll down to the Security section and tap "Stolen Device Protection".',
    image: stolenDeviceTapStolen,
    pointerTarget: [50, 85] as [number, number], // Points to Stolen Device Protection row
  },
  {
    step: 'Step 4',
    title: 'Turn Off Stolen Device Protection',
    content: 'Tap the toggle to turn off Stolen Device Protection.',
    image: stolenDeviceToggle,
    pointerTarget: [88, 18] as [number, number], // Points to the toggle switch
  },
  {
    step: 'Step 5',
    title: 'Start the Security Delay',
    content: 'Tap "Start Security Delay" to begin the 1-hour waiting period.',
    image: stolenDeviceOff,
    pointerTarget: [50, 88] as [number, number], // Points to Start Security Delay button
  },
  {
    step: 'Step 6',
    title: 'Wait for the Delay',
    content: 'Wait for the 1-hour security delay to complete before continuing.',
    image: stolenDeviceWaiting,
    pointerTarget: [50, 82] as [number, number], // Points to Done button
  },
  {
    step: 'Step 7',
    title: 'Turn Off Stolen Device Protection',
    content: 'After the hour, go back to Stolen Device Protection and tap the toggle to turn it off.',
    image: stolenDeviceTurnOffAfterDelay,
    pointerTarget: [88, 18] as [number, number], // Points to the toggle
  },
];

type GuideMode = 'main' | 'stolen-device' | 'waiting';

const FindMyGuideModal: React.FC<FindMyGuideModalProps> = ({ open, onOpenChange }) => {
  const [mode, setMode] = useState<GuideMode>('main');

  const handleCantTurnOff = () => {
    setMode('stolen-device');
  };

  // Create steps with action callback
  const findMySteps = createFindMySteps(handleCantTurnOff);

  const handleReturnToFindMy = () => {
    setMode('main');
  };

  const handleClose = () => {
    setMode('main');
    onOpenChange(false);
  };

  const handleStolenDeviceComplete = () => {
    // Return to the main Find My flow at step 1
    setMode('main');
  };

  // Waiting screen after disabling Stolen Device Protection
  if (mode === 'waiting') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Clock className="w-6 h-6 text-amber-500" />
              Waiting Period Required
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Important:</strong> After disabling Stolen Device Protection, you must wait <strong>1 hour</strong> before you can turn off Find My iPhone.
              </AlertDescription>
            </Alert>

            <div className="bg-muted/50 rounded-lg p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Come back in 1 hour
                </h3>
                <p className="text-muted-foreground">
                  Once the hour has passed, return to this screen and complete the steps to turn off Find My iPhone.
                </p>
              </div>
            </div>

            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Don't worry!</strong> You will be able to turn Find My iPhone back on after Shomri is installed.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col gap-2 pt-4">
              <Button onClick={handleReturnToFindMy} className="w-full">
                I've Waited - Continue to Find My iPhone
              </Button>
              <Button variant="outline" onClick={handleClose} className="w-full">
                Close for Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        {/* Back button for stolen device mode */}
        {mode === 'stolen-device' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMode('main')}
            className="absolute top-4 left-4 z-10 gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
        )}

        <div>
          {/* Reassurance message - only show on main flow */}
          {mode === 'main' && (
            <div className="px-5 pt-5 pb-2">
              <Alert className="border-primary/20 bg-primary/5">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <AlertDescription className="text-foreground text-sm">
                  You can turn Find My iPhone back on after Shomri is installed.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Stolen Device Protection context */}
          {mode === 'stolen-device' && (
            <div className="px-5 pt-12 pb-2">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  Stolen Device Protection is preventing Find My iPhone from being disabled. Follow these steps first.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Feature Steps Component */}
          <FeatureSteps
            features={mode === 'main' ? findMySteps : STOLEN_DEVICE_STEPS}
            title={mode === 'main' ? 'Disable Find My iPhone' : 'Disable Stolen Device Protection'}
            imageHeight="h-auto"
            onComplete={mode === 'stolen-device' ? handleStolenDeviceComplete : handleClose}
            completeButtonLabel={mode === 'stolen-device' ? 'Turn Off Find My iPhone' : 'Done'}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FindMyGuideModal;
